/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office */

// Side-effect import first so Sentry.init runs before the rest of the app.
import './sentry.js';
import { setSentryUser, captureException } from './sentry.js';
import { fetchContactsByEmail } from './services/api.js';
import { createLoginScreen } from './components/login.js';
import { login, isAuthenticated, getInstance, getUserEmail } from './services/auth.js';
import { createHeader } from './components/header.js';
import { createLoadingComponent } from './components/loading.js';
import { createErrorSection } from './components/error-section.js';
import { ImportWizard } from './components/wizard/wizard.js';
import { ImportStatusService } from './services/import-status.js';
import { EmailExtractionService } from './utils/email-extraction.js';
import { CRMNavigationService } from './services/crm-navigation.js';
import { initFloatingAIButton, removeFloatingAIButton } from './components/ai-assistant/floating-ai-button.js';
import { loadAiAssistantGate } from './services/domain/ai-credits.js';
import { t, getLanguage } from './i18n.js';

/**
 * Application orchestrator
 * Follows Single Responsibility Principle by coordinating services without containing business logic
 */
class TaskPaneApp {
  constructor() {
    this.importStatusService = new ImportStatusService();
    this.emailExtractionService = new EmailExtractionService();
    this.crmNavigationService = new CRMNavigationService();
    this.wizard = null;
  }

  _disposeWizard() {
    this.wizard?.dispose();
    this.wizard = null;
  }

  /**
   * Initialize the application
   */
  async initialize() {
    try {
      if (isAuthenticated()) {
        setSentryUser({ email: getUserEmail(), instance: getInstance() });
      }

      Office.onReady((info) => {
        if (info.host === Office.HostType.Outlook) {
          document.documentElement.lang = getLanguage() === 'de' ? 'de' : 'en';
          document.getElementById("sideload-msg").style.display = "none";
          document.getElementById("app-body").style.display = "flex";
          this.run();
          Office.context.mailbox.addHandlerAsync(
            Office.EventType.ItemChanged,
            () => this.run()
          );
        }
      });
    } catch (error) {
      console.error('Failed to initialize Office:', error);
      captureException(error);
    }
  }

  /**
   * Handle user login
   */
  async handleLogin(email, password, instance) {
  try {
    await login(email, password, instance);
      await this.run();
  } catch (error) {
    // Re-throw the error so the login component can handle it
    // This preserves device verification errors and other error types
    throw error;
  }
}

  /**
   * Handle successful login completion (after verification or regular login)
   */
  async handleLoginSuccess() {
    await this.run();
  }

  /**
   * Open activity in CRM
   */
  async openActivityInCRM(activityId) {
    await this.crmNavigationService.openActivity(activityId, getInstance);
  }

  /**
   * Main application flow
   */
  async run() {
  this._disposeWizard();
  try {
    const container = document.getElementById("app-body");
    
    // Check if user is authenticated
    if (!isAuthenticated()) {
      removeFloatingAIButton();
      container.innerHTML = '';
        container.appendChild(
          createLoginScreen(
            this.handleLogin.bind(this), 
            this.handleLoginSuccess.bind(this)
          )
        );
      return;
    }

    const item = Office.context.mailbox.item;
    
    // Check if item context is available
    if (!item) {
      console.error('Office.context.mailbox.item is undefined');
      const errorSection = createErrorSection(t('no_item_selected'));
      container.innerHTML = '';
      container.appendChild(errorSection);
      return;
    }
    
    const loggedInEmail = Office.context.mailbox.userProfile.emailAddress?.toLowerCase() || '';
    const isAppointment = item.itemType === Office.MailboxEnums.ItemType.Appointment;
    
      // Extract emails using the extraction service
      const allEmails = await this.emailExtractionService.extractEmails(item, loggedInEmail);
      
      // Setup UI
      await this._setupUI(container, item, isAppointment, allEmails);
      
    } catch (error) {
      console.error('=== ADD-IN ERROR ===', error);
      console.error('Error stack:', error.stack);
      const container = document.getElementById("app-body");
      const errorSection = createErrorSection(error.message);
      container.innerHTML = '';
      container.appendChild(errorSection);
    }
  }

  /**
   * Setup the user interface
   */
  async _setupUI(container, item, isAppointment, allEmails) {
    // Create main layout
    container.innerHTML = '';
    container.style.flexDirection = 'column';
    container.style.width = '100%';
    
    // Create and add header
    const header = createHeader();
    container.appendChild(header);
    
    // Check and display import status
    await this._displayImportStatus(container, item, isAppointment);
    
    // Create main content container
    const mainContent = document.createElement('div');
    mainContent.className = 'main-content';
    
    // Show loading state
    const loadingComponent = createLoadingComponent();
    mainContent.appendChild(loadingComponent);
    container.appendChild(mainContent);
    
    // Fetch contacts and display wizard
    await this._displayWizard(mainContent, item, allEmails);
    
    // Same visibility as WEB-CRM: canUseAI && tenant aiAssistantEnabled.
    // Exhausted credits still show the button; click opens the usage-limit modal.
    setTimeout(() => {
      loadAiAssistantGate().then((show) => {
        if (show) initFloatingAIButton();
        else removeFloatingAIButton();
      });
    }, 100);
  }

  /**
   * Display import status tag
   */
  async _displayImportStatus(container, item, isAppointment) {
    const importStatusTag = await this.importStatusService.createImportStatusTag(
      item, 
      isAppointment,
      this.openActivityInCRM.bind(this)
    );
    
    if (importStatusTag) {
      container.appendChild(importStatusTag);
    }
  }

  /**
   * Fetch contacts and display wizard
   */
  async _displayWizard(mainContent, item, allEmails) {
    // Fetch contacts from API
    const contacts = (await fetchContactsByEmail(allEmails))?.contacts || [];
    
    // Clear loading and display wizard
    mainContent.innerHTML = '';
    
    this._disposeWizard();
    this.wizard = new ImportWizard(item, contacts, allEmails);
    await this.wizard.init(mainContent);
  }
}

// Create and initialize the application
const app = new TaskPaneApp();
app.initialize();

// Export run function for backward compatibility
export async function run() {
  await app.run();
}